import {MessageBox} from 'element-ui'

import {APP_VERSION, VERSION_ENDPOINT} from '@/config/appVersion'

const CHECK_INTERVAL_MS = Number(process.env.VUE_APP_VERSION_CHECK_INTERVAL_MS || 5 * 60 * 1000)
const FOCUS_CHECK_GAP_MS = 30 * 1000

let checking = false
let prompting = false
let lastCheckAt = 0
let timer = null

function normalizeVersion(version) {
    return String(version || '').trim()
}

function isChunkLoadError(error) {
    const message = String(error?.message || error || '')
    return /Loading chunk \d+ failed|ChunkLoadError|failed to fetch dynamically imported module|Importing a module script failed/i.test(message)
}

function shouldPromptRefresh(remoteVersion) {
    const currentVersion = normalizeVersion(APP_VERSION)
    const latestVersion = normalizeVersion(remoteVersion)
    return currentVersion && latestVersion && currentVersion !== 'dev' && latestVersion !== 'dev' && currentVersion !== latestVersion
}

async function fetchLatestVersion() {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
        return null
    }

    const separator = VERSION_ENDPOINT.includes('?') ? '&' : '?'
    const response = await window.fetch(`${VERSION_ENDPOINT}${separator}t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
            Accept: 'application/json',
        },
    })

    if (!response.ok) {
        return null
    }

    try {
        return await response.json()
    } catch (error) {
        return null
    }
}

function reloadPage() {
    window.location.reload()
}

export function requestVersionRefresh(message, options = {}) {
    if (prompting || typeof window === 'undefined') {
        return
    }

    prompting = true
    const force = options.force === true
    const promptMessage = message || '系统已发布新版本，请刷新页面后继续使用。'

    const dialog = force
        ? MessageBox.alert(promptMessage, '系统版本已更新', {
            confirmButtonText: '立即刷新',
            type: 'warning',
            closeOnClickModal: false,
            closeOnPressEscape: false,
            showClose: false,
        })
        : MessageBox.confirm(promptMessage, '系统版本已更新', {
            confirmButtonText: '立即刷新',
            cancelButtonText: '稍后刷新',
            type: 'warning',
            closeOnClickModal: false,
        })

    dialog.then(reloadPage).catch(() => {
        prompting = false
    })
}

async function checkVersion() {
    if (checking) {
        return
    }

    checking = true
    lastCheckAt = Date.now()
    try {
        const latest = await fetchLatestVersion()
        if (shouldPromptRefresh(latest?.version)) {
            requestVersionRefresh('系统已发布新版本。为避免旧页面与新接口不兼容，请刷新后继续使用。')
        }
    } finally {
        checking = false
    }
}

function checkVersionWithGap() {
    if (Date.now() - lastCheckAt < FOCUS_CHECK_GAP_MS) {
        return
    }
    checkVersion()
}

function installChunkErrorGuard() {
    window.addEventListener('error', (event) => {
        if (isChunkLoadError(event.error || event.message)) {
            requestVersionRefresh('页面资源已更新，请刷新后继续使用。', {force: true})
        }
    })

    window.addEventListener('unhandledrejection', (event) => {
        if (isChunkLoadError(event.reason)) {
            requestVersionRefresh('页面资源已更新，请刷新后继续使用。', {force: true})
        }
    })
}

export function initVersionGuard(router) {
    if (typeof window === 'undefined' || window.__qcyyVersionGuardInstalled) {
        return
    }

    window.__qcyyVersionGuardInstalled = true
    installChunkErrorGuard()

    // 通过多入口触发版本检查，覆盖长时间不刷新的后台标签页。
    window.addEventListener('focus', checkVersionWithGap)
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            checkVersionWithGap()
        }
    })

    if (router && typeof router.afterEach === 'function') {
        router.afterEach(checkVersionWithGap)
    }

    timer = window.setInterval(checkVersion, CHECK_INTERVAL_MS)
    window.setTimeout(checkVersion, 3000)
}

export function disposeVersionGuard() {
    if (timer) {
        window.clearInterval(timer)
        timer = null
    }
}
